// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { IdentificationCard } from "@phosphor-icons/react";
import { Button, Heading, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

export const ASK_V2_WELCOME_CHIPS = [
  { id: "know", labelKey: "askV2.home.welcome.know" },
  { id: "personalize", labelKey: "askV2.home.welcome.personalize" },
  { id: "canDo", labelKey: "askV2.home.welcome.canDo" },
] as const;

type WelcomeBriefingProps = {
  disabled?: boolean;
  onSelect: (prompt: string) => void;
  /** Personalized subheading from christo; null keeps the translated default. */
  title?: string | null;
  /** Personalized summary from christo; null keeps the translated default. */
  summary?: string | null;
};

function personalizedOrDefault(
  personalized: string | null | undefined,
  fallback: string,
): { text: string; personalized: boolean } {
  const trimmed = personalized?.trim();
  return trimmed
    ? { text: trimmed, personalized: true }
    : { text: fallback, personalized: false };
}

export function WelcomeBriefing({
  disabled = false,
  onSelect,
  title = null,
  summary = null,
}: WelcomeBriefingProps) {
  const { t } = useTranslation("ui");
  const heading = personalizedOrDefault(title, t("askV2.home.welcome.title"));
  const body = personalizedOrDefault(summary, t("askV2.home.welcome.summary"));

  return (
    <section
      data-testid="ask-v2-welcome"
      data-personalized={
        heading.personalized || body.personalized ? "true" : "false"
      }
      className="flex w-full max-w-3xl flex-col items-center gap-3 text-center"
    >
      <Heading as="h2" size="5" data-testid="ask-v2-welcome-title">
        {heading.text}
      </Heading>
      <Text
        as="p"
        size="3"
        className="text-puntt-neutral-gray-11"
        data-testid="ask-v2-welcome-summary"
      >
        {body.text}
      </Text>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
        {ASK_V2_WELCOME_CHIPS.map((chip) => {
          const label = t(chip.labelKey);
          return (
            <Button
              key={chip.id}
              type="button"
              size="3"
              variant="outline"
              data-testid={`ask-v2-welcome-chip-${chip.id}`}
              data-chip={chip.id}
              disabled={disabled}
              onClick={() => onSelect(label)}
            >
              <IdentificationCard />
              {label}
            </Button>
          );
        })}
      </div>
    </section>
  );
}
